// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Loader, Stack } from '@mantine/core';
import type { Appointment, Encounter } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { IntakeScreenerModal } from '../../../components/encounter/IntakeScreenerModal';
import { CompletedIntakeScreenerModal } from '../../../components/encounter/SupplementalInfoSection';
import { PatientTreatmentsSection } from '../../../components/patient/PatientTreatmentsSection';
import { useCareTeamMembers } from '../../../hooks/useCareTeamMembers';
import { usePatient } from '../../../hooks/usePatient';
import { usePatientAssessments } from '../../../hooks/usePatientAssessments';
import { usePatientConditions } from '../../../hooks/usePatientConditions';
import { usePatientDiagnoses } from '../../../hooks/usePatientDiagnoses';
import { usePatientDocuments } from '../../../hooks/usePatientDocuments';
import { usePatientEncounterNotes } from '../../../hooks/usePatientEncounterNotes';
import { usePatientMedications } from '../../../hooks/usePatientMedications';
import { usePatientPrescriptions } from '../../../hooks/usePatientPrescriptions';
import { useSupplementalInformation } from '../../../hooks/useSupplementalInformation';
import { AssessmentsSection } from './AssessmentsSection';
import { CareTeamSection } from './CareTeamSection';
import { ConditionsSection } from './ConditionsSection';
import { DiagnosesSection } from './DiagnosesSection';
import { DocumentsSection } from './DocumentsSection';
import { EncounterNotesSection } from './EncounterNotesSection';
import { MedicationsSection } from './MedicationsSection';
import { PrescriptionsSection } from './PrescriptionsSection';
import { RpmHistorySection } from './RpmHistorySection';
import { SupplementalInformationSection } from './SupplementalInformationSection';

const TAG_SYSTEM = 'http://polarissleep.com/tags';
const FINISHED_CODE = 'eligibility-check-finished';

interface MedicalInfoTabProps {
  encounter?: Encounter;
  appointment?: Appointment;
}

export function MedicalInfoTab({ encounter, appointment }: MedicalInfoTabProps = {}): JSX.Element {
  const patient = usePatient();
  const [screenerOpened, setScreenerOpened] = useState(false);
  const intakeCompleted = patient?.meta?.tag?.some((tag) => tag.system === TAG_SYSTEM && tag.code === FINISHED_CODE);

  const supplemental = useSupplementalInformation(patient);
  const careTeam = useCareTeamMembers(patient);
  const diagnoses = usePatientDiagnoses(patient);
  const conditions = usePatientConditions(patient);
  const medications = usePatientMedications(patient);
  const prescriptions = usePatientPrescriptions(patient);
  const assessments = usePatientAssessments(patient);
  const patientDocuments = usePatientDocuments(patient);
  const encounterNotes = usePatientEncounterNotes(patient);

  if (!patient) {
    return (
      <Stack align="center" p="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <SupplementalInformationSection
        intakeComposition={supplemental.intakeComposition}
        hsatDiagnosticReport={supplemental.hsatDiagnosticReport}
        patientId={patient.id ?? ''}
        loading={supplemental.loading}
        intakeCompleted={intakeCompleted}
        onOpenScreener={() => setScreenerOpened(true)}
      />
      {intakeCompleted ? (
        <CompletedIntakeScreenerModal
          patientId={patient.id ?? ''}
          opened={screenerOpened}
          onClose={() => setScreenerOpened(false)}
        />
      ) : (
        <IntakeScreenerModal
          patient={patient}
          encounter={encounter}
          opened={screenerOpened}
          onClose={() => setScreenerOpened(false)}
        />
      )}
      <CareTeamSection
        patient={patient}
        careTeam={careTeam.careTeam}
        members={careTeam.members}
        loading={careTeam.loading}
        onCareTeamChanged={careTeam.setCareTeam}
        onRefresh={careTeam.refresh}
      />
      <DiagnosesSection
        patient={patient}
        diagnoses={diagnoses.diagnoses}
        loading={diagnoses.loading}
        onChanged={diagnoses.setDiagnoses}
        onRefresh={diagnoses.refresh}
      />
      <ConditionsSection
        patient={patient}
        conditions={conditions.conditions}
        loading={conditions.loading}
        onChanged={conditions.setConditions}
        onRefresh={conditions.refresh}
      />
      <RpmHistorySection patient={patient} />
      <PatientTreatmentsSection patient={patient} place="profile" />
      <MedicationsSection
        patient={patient}
        medications={medications.medications}
        loading={medications.loading}
        onChanged={medications.setMedications}
        onRefresh={medications.refresh}
      />
      <PrescriptionsSection
        patient={patient}
        prescriptions={prescriptions.prescriptions}
        loading={prescriptions.loading}
        onRefresh={prescriptions.refresh}
        documents={patientDocuments.documents}
      />
      <DocumentsSection
        patient={patient}
        documents={patientDocuments.documents}
        loading={patientDocuments.loading}
        onChanged={patientDocuments.setDocuments}
        onRefresh={patientDocuments.refresh}
      />
      <EncounterNotesSection
        patientId={patient.id ?? ''}
        notes={encounterNotes.notes}
        loading={encounterNotes.loading}
      />
      <AssessmentsSection
        patient={patient}
        patientId={patient.id ?? ''}
        assessments={assessments.assessments}
        loading={assessments.loading}
        onRefresh={assessments.refresh}
        onDocumentCreated={patientDocuments.refresh}
      />
    </Stack>
  );
}
